import * as _ from 'lodash'
import React, { useState } from 'react';
import { useJobCostItemDragDrop } from '../draggableJobCostLineItem';
import valueFilters from '../valueFilters';
import globalRelay from '../globalRelay';
import enums from '../enum';
import keyboardUtil from 'Scripts/keyboardUtils';
import * as extensions from './extensions';
import * as vendorActions from '../vendorActions';import jobCostSpecificationFactory from '../models/jobCostSpecificationModel';
import { FractionInput, Header, jobCostRowOwner, JobCostHiddenWhenLocked } from './sharedComponents';

const KEY_DOWN_CODES = [13];

const JobCostApplianceRow = (extensions as any).JobCostApplianceRow || function (props) {
	const globalConfig = globalRelay.config!;
	const { alwaysValidate, buttonHandler, classNames = [], dropLineItem, buttonClass, draggable, applianceSpec, updateModel, workAreas } = props;
	const [shouldValidate, setShouldValidate] = useState(alwaysValidate);
	const [{isDragging, dragSourceRef, dragPreviewRef, dropItem, dropTargetRef, rowRef}] = useJobCostItemDragDrop({ 
		item: applianceSpec,
		handleDrop: (dragItem, dropItem) => dropLineItem(dragItem.key, dropItem.key) 
	});

	function handleUpdate (path, autoSave, e) {
		e.preventDefault();

		let newVal;
		if (path === 'note') {
			newVal = (autoSave) ? valueFilters.trim(e.target.value) : e.target.value;
		}
		else {
			newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;
		}

		updateModel(model => {
			const sizeMatch = /(applianceSize|openingSize)\/(.*)/.exec(path);
			if(sizeMatch?.length) {
				/**
				 * Don't love this but it works with contractProxy.ts to avoid a bug when updating the sizes on specs.
				 * This ensures that the slight of hand provided by the proxy works in conjunction with a non-proxied child model (applianceSize/openingSize)
				 */
				const target = model[sizeMatch[1]];
				
				target[sizeMatch[2]] = newVal;

				model[sizeMatch[1]] = target;
			}
			else {
				model.setAt(path, newVal);
			}
		}, { autoSave: autoSave });
	}

	function validate (property?) {
		if (!property || shouldValidate) {
			const result = !applianceSpec.test(property);

			return result;
		}
		return true;
	}

	function handleButtonClick (e) {
		e.preventDefault();
		const valid = validate();

		if (!valid) {
			setShouldValidate(true);
		}
		else {
			// this assumes that the add handler succeeds
			setShouldValidate(alwaysValidate);
		}

		buttonHandler(valid, e);
	}

	// Define the work area options list
	const workAreaOptions:JSX.Element[] = [];

	workAreaOptions.push(<option key="unknown_workarea" value="">Select</option>);
	_.forEach(workAreas, workArea => {
		workAreaOptions.push(<option key={workArea.key} value={workArea.key}>{workArea.name}</option>);
	});

	const disableItemEntry = (applianceSpec.name === undefined) ? 'disabled' : '';

	// Configure the default appliances
	const applianceOptions:JSX.Element[] = [];

	_.forEach(globalConfig.jobCostSettings.specifications.defaultAppliances, a => {
		applianceOptions.push(<option key={a} value={a}>{a}</option>);
	});

	return (
		<tr className={ classNames.join(' ') }>
			<td />
			<td>
				<select className={!validate('workAreaKey') ? 'required' : ''} placeholder="Select" value={applianceSpec.viewValue('workAreaKey')} onChange={e => handleUpdate('workAreaKey', true, e)}>
					{ workAreaOptions }
				</select>
			</td>
			<td>
				<input type="text" className="dataList" list={'spec_' + applianceSpec.key} 
					placeholder="Enter or Select" value={applianceSpec.viewValue('name')} 
					onChange={e => handleUpdate('name', false, e)} 
					onBlur={e => handleUpdate('name', true, e)}
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
				<datalist id={'spec_' + applianceSpec.key}>
					{ applianceOptions }
				</datalist>
			</td>
			<td>
				<input type="text" disabled={!!disableItemEntry} className={disableItemEntry} placeholder="Enter" 
					autoCapitalize="" autoCorrect="off" value={applianceSpec.viewValue('value')} 
					onChange={e => handleUpdate('value', false, e)} 
					onBlur={e => handleUpdate('value', true, e)} 
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
			</td>
			<td className={'lwh' + (!validate('applianceSize') ? ' required' : '')}>
				<FractionInput className={disableItemEntry} placeholder="W" 
					value={applianceSpec.applianceSize.viewValue('width')} 
					onChange={e => handleUpdate('applianceSize/width', false, e)} 
					onBlur={e => handleUpdate('applianceSize/width', true, e)} 
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
				<FractionInput className={disableItemEntry} placeholder="H" 
					value={applianceSpec.applianceSize.viewValue('height')} 
					onChange={e => handleUpdate('applianceSize/height', false, e)} 
					onBlur={e => handleUpdate('applianceSize/height', true, e)}
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
				<FractionInput className={disableItemEntry} placeholder="D" 
					value={applianceSpec.applianceSize.viewValue('depth')} 
					onChange={e => handleUpdate('applianceSize/depth', false, e)} 
					onBlur={e => handleUpdate('applianceSize/depth', true, e)}
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />

			</td>
			<td className={'lwh' + (!validate('openingSize') ? ' required' : '')}>
				<FractionInput className={disableItemEntry} placeholder="W" 
					value={applianceSpec.openingSize.viewValue('width')} 
					onChange={e => handleUpdate('openingSize/width', false, e)} 
					onBlur={e => handleUpdate('openingSize/width', true, e)} 
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
				<FractionInput className={disableItemEntry} placeholder="H" 
					value={applianceSpec.openingSize.viewValue('height')} 
					onChange={e => handleUpdate('openingSize/height', false, e)} 
					onBlur={e => handleUpdate('openingSize/height', true, e)} 
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
				<FractionInput className={disableItemEntry} placeholder="D" 
					value={applianceSpec.openingSize.viewValue('depth')} 
					onChange={e => handleUpdate('openingSize/depth', false, e)} 
					onBlur={e => handleUpdate('openingSize/depth', true, e)}
					onKeyDown={(e) => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, () => handleButtonClick(e))} />
			</td>
			<td><textarea style={classNames.indexOf('readOnly') !== -1 ? {pointerEvents: 'stroke'} :{}} className={ `lineItemDescription ${disableItemEntry}` } placeholder="Enter" value={ applianceSpec.viewValue('note') } onChange={e => handleUpdate('note', false, e)} onBlur={e => handleUpdate('note', true, e)} /></td>
			<td><button className={buttonClass} onClick={handleButtonClick}><span /></button></td>
			<td />
		</tr>
	);
}

export default jobCostRowOwner({ listAccessor: model => model.specifications, listFilterThunkFactory: () => row => row.group === enums.specGroups.Appliance }, class JobCostEditApplianceSpecs extends React.Component<any,any> {
	constructor (props) {
		super(props);

		this.state = this.getStateFromProps(props);
	}

	componentDidMount () {
		vendorActions.fetchOutsideSuppliers();
	}

	getStateFromProps (props) {
		const state = _.assign({}, this.state);

		if (!state.pendingApplianceSpec) {
			state.pendingApplianceSpec = this.createPendingApplianceSpec();
		}

		return state;
	}

	createPendingApplianceSpec () {
		return jobCostSpecificationFactory({ group: enums.specGroups.Appliance });
	}

	addApplianceSpecHandler (applianceSpecKey, isValid) {
		if (!isValid) {
			return;
		}

		this.props.updateModel(model => {
			if (applianceSpecKey === this.state.pendingApplianceSpec.key) {
				this.state.pendingApplianceSpec.sortIndex = _.filter(model.specifications, f => f.group === enums.specGroups.Appliance).length;

				model.specifications.add(this.state.pendingApplianceSpec);

				this.props.sortRows(model);

				this.setState({ pendingApplianceSpec: this.createPendingApplianceSpec() });
			}
		}, { autoSave: true });
	}

	updateModel (key, updater, options) {
		this.props.updateModel(jobCost => {
			let applianceSpec = jobCost.specifications.get(key);

			if ((!applianceSpec) && (key === this.state.pendingApplianceSpec.key)) {
				applianceSpec = this.state.pendingApplianceSpec;
				options.autoSave = false;
			}

			if (applianceSpec) {
				updater(applianceSpec);
			}
		}, options);
	}

	render () {
		const { getRowClasses, jobCost, rowContextMenuHandler } = this.props;

		const applianceSpecifications = _.filter(jobCost.specifications, f => { return f.group === enums.specGroups.Appliance;});


		const specifications = _.map(_.sortBy(applianceSpecifications, 'sortIndex'), applianceSpec => {
			return (
				<JobCostApplianceRow key={applianceSpec.rowKey} 
					updateModel={this.updateModel.bind(this, applianceSpec.key)} 
					buttonHandler={ rowContextMenuHandler.bind(this, applianceSpec) } 
					applianceSpec={applianceSpec} 
					classNames={ getRowClasses({ item: applianceSpec, baseClassNames: ['added']}) } 
					buttonClass="icon iconOnly contextMenu" draggable={false} 
					alwaysValidate={true} workAreas={jobCost.workAreas}/>
			);
		});

		return (
			<section className="jobCostApplianceSpecs">
				<Header title="Appliance Specs" />
				<section>
					<div className="inner">
						<table className="compact" cellPadding="0" cellSpacing="0">
							<thead>
								<tr>
									<td />
									<td><span className="readOnly">Work Area</span></td>
									<td><span className="readOnly">Appliance Name</span></td>
									<td><span className="readOnly">Description</span></td>
									<td className="lwh"><span className="readOnly">Size (WHD)</span></td>
									<td className="lwh"><span className="readOnly">Opening (WHD)</span></td>
									<td><span className="readOnly">Application Notes</span></td>
									<td className="action" />
									<td />
								</tr>
							</thead>
							<tbody>
								<JobCostHiddenWhenLocked jobCost={ jobCost }>
									<JobCostApplianceRow key={this.state.pendingApplianceSpec.key} 
										updateModel={this.updateModel.bind(this, this.state.pendingApplianceSpec.key)} 
										buttonHandler={this.addApplianceSpecHandler.bind(this, this.state.pendingApplianceSpec.key)} 
										classNames={ ['addRow'] } applianceSpec={this.state.pendingApplianceSpec} 
										buttonClass="icon iconOnly addItem" workAreas={jobCost.workAreas}/>
								</JobCostHiddenWhenLocked>
								{specifications}
							</tbody>
						</table>
					</div>
				</section>
			</section>

		);
	}
});
